import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/navbar';
import Footer from '@/components/Footer';
import { useAuthCheck } from '@/components/tracks/useAuthCheck';
import { useToast } from '@/components/ui/use-toast';
import { useSubscriptionData } from '@/hooks/useSubscriptionData';
import CurrentPlan from '@/components/subscription/CurrentPlan';
import CancelSubscriptionDialog from '@/components/subscription/CancelSubscriptionDialog';
import { supabase } from '@/integrations/supabase/client';

const ManageSubscription: React.FC = () => {
  const { isLoading: isAuthLoading } = useAuthCheck();
  const { subscription, isLoading: isSubscriptionLoading, refreshSubscription } = useSubscriptionData();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleManagePaymentMethod = async () => {
    setIsUpdating(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Authentication required');
      }
      
      // Call the Supabase Edge Function to create a customer portal session
      const { data, error } = await supabase.functions.invoke('create-customer-portal', {
        body: { 
          returnUrl: window.location.origin + '/manage-subscription'
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data?.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }
      
    } catch (error: any) {
      console.error('Error creating customer portal session:', error);
      toast({
        title: "Error",
        description: error.message || "Something went wrong.",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelSubscription = async (shouldRefund = false) => {
    setIsUpdating(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Authentication required');
      }
      
      // Call the cancel-subscription edge function
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { 
          userId: session.user.id,
          stripeSubscriptionId: subscription?.stripeSubscriptionId || null,
          shouldRefund
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      toast({
        title: "Subscription Canceled",
        description: "Your subscription has been canceled successfully.",
      });
      
      // After successful cancellation, refresh the subscription data
      await refreshSubscription();
      setShowCancelDialog(false);
      
    } catch (error: any) {
      console.error('Error cancelling subscription:', error);
      toast({
        title: "Error cancelling subscription",
        description: error.message || "Something went wrong.",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const isLoading = isAuthLoading || isSubscriptionLoading || isUpdating;

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-20">
        <div className="container px-4 mx-auto">
          <div className="w-full max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold mb-8">Manage Subscription</h1>
            
            <CurrentPlan 
              subscription={subscription}
              isLoading={isLoading}
              onManagePaymentMethod={handleManagePaymentMethod}
              onCancelSubscription={() => setShowCancelDialog(true)}
            />
          </div>
        </div>
      </main>
      
      <CancelSubscriptionDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onCancel={handleCancelSubscription}
        isUpdating={isUpdating}
        hasStripeSubscription={!!subscription?.stripeSubscriptionId}
      />
      
      <Footer />
    </div>
  );
};

export default ManageSubscription;
